import Color from 'color';
/**
 * CSS variables derived from the primary colour. Keep in sync with the
 * defaults declared in styles/common/base.scss.
 */
export const PRIMARY_COLOR_VARIABLES = [
    '--primary',
    '--primary-color',
    '--primary-rgb',
    '--primary-contrast',
    '--primary-50',
    '--primary-100',
    '--primary-200',
    '--primary-300',
    '--primary-400',
    '--primary-500',
    '--primary-600',
    '--primary-700',
    '--primary-800',
    '--primary-light',
    '--primary-fade',
    '--primary-dark',
    '--primary-darker'
];
/**
 * CSS variables derived from the accent and brand surface colours. Defaults
 * (falling back on the primary colour and gray-800) live in
 * styles/common/base.scss.
 */
export const ACCENT_COLOR_VARIABLES = [
    '--accent',
    '--accent-rgb',
    '--accent-contrast',
    '--accent-100',
    '--accent-600'
];
export const SURFACE_COLOR_VARIABLES = [
    '--surface-brand',
    '--surface-brand-contrast',
    '--surface-brand-muted',
    '--surface-brand-line'
];
const LIGHT_TEXT = '#ffffff';
const DARK_TEXT = '#20262b';
/**
 * @function normalizeColor
 * @since 1.0.0
 * Accepts `#f05b22`, `f05b22` or any CSS colour; returns a hex string, or
 * null when the value is empty or not a colour.
 */
export function normalizeColor(value) {
    if (!value) {
        return null;
    }
    const candidate = /^[0-9a-f]{3,8}$/i.test(value) ? `#${value}` : value;
    try {
        return new Color(candidate).hex().toLowerCase();
    }
    catch (e) {
        return null;
    }
}
/**
 * @function resolvePrimaryColor
 * @since 1.0.0
 * First valid colour among the candidates (e.g. account, then domain).
 */
export function resolvePrimaryColor(...candidates) {
    for (const candidate of candidates) {
        const color = normalizeColor(candidate);
        if (color) {
            return color;
        }
    }
    return null;
}
/**
 * Minimum WCAG contrast for white text before switching to dark text (AA for
 * large or bold text, which covers buttons and badges). Keeps white on most
 * saturated brand colours, such as the default orange.
 */
export const MIN_TEXT_CONTRAST = 3;
/**
 * @function contrastText
 * @since 1.0.0
 * Text colour to put on the given background: white while it stays readable,
 * otherwise whichever of white or near black contrasts best.
 */
export function contrastText(hex) {
    const base = new Color(hex);
    const light = base.contrast(new Color(LIGHT_TEXT));
    if (light >= MIN_TEXT_CONTRAST) {
        return LIGHT_TEXT;
    }
    return light >= base.contrast(new Color(DARK_TEXT)) ? LIGHT_TEXT : DARK_TEXT;
}
/**
 * @function contrastRatio
 * @since 1.0.0
 * WCAG contrast ratio between a background and its readable text colour.
 */
export function contrastRatio(hex) {
    return new Color(hex).contrast(new Color(contrastText(hex)));
}
/**
 * @function primaryPalette
 * @since 1.0.0
 * Tints (mixed with white) and shades (mixed with black) of the primary
 * colour, plus an rgb triplet and a readable text colour to put on it.
 */
export function primaryPalette(hex) {
    const base = new Color(hex);
    const white = new Color('#ffffff');
    const black = new Color('#000000');
    const tint = (weight) => base.mix(white, weight).hex().toLowerCase();
    const shade = (weight) => base.mix(black, weight).hex().toLowerCase();
    const [r, g, b] = base.rgb().array().map(Math.round);
    const rgb = `${r}, ${g}, ${b}`;
    const color = base.hex().toLowerCase();
    return {
        '--primary': color,
        '--primary-color': color,
        '--primary-rgb': rgb,
        '--primary-contrast': contrastText(color),
        '--primary-50': tint(0.95),
        '--primary-100': tint(0.9),
        '--primary-200': tint(0.8),
        '--primary-300': tint(0.65),
        '--primary-400': color,
        '--primary-500': shade(0.06),
        '--primary-600': shade(0.14),
        '--primary-700': shade(0.24),
        '--primary-800': shade(0.34),
        '--primary-light': `rgba(${rgb}, 0.1)`,
        '--primary-fade': `rgba(${rgb}, 0.06)`,
        '--primary-dark': shade(0.14),
        '--primary-darker': shade(0.24)
    };
}
/**
 * @function accentPalette
 * @since 1.0.0
 * Accent colour (badges, promotions, completed steps), its rgb triplet and
 * readable text colour.
 */
export function accentPalette(hex) {
    const base = new Color(hex);
    const [r, g, b] = base.rgb().array().map(Math.round);
    const color = base.hex().toLowerCase();
    return {
        '--accent': color,
        '--accent-rgb': `${r}, ${g}, ${b}`,
        '--accent-contrast': contrastText(color),
        '--accent-100': base.mix(new Color('#ffffff'), 0.9).hex().toLowerCase(),
        '--accent-600': base.mix(new Color('#000000'), 0.14).hex().toLowerCase()
    };
}
/**
 * @function surfacePalette
 * @since 1.0.0
 * Brand surface (footer, confirmation banner) with its text colours.
 */
export function surfacePalette(hex) {
    const color = new Color(hex).hex().toLowerCase();
    const text = new Color(contrastText(color));
    const [r, g, b] = text.rgb().array().map(Math.round);
    return {
        '--surface-brand': color,
        '--surface-brand-contrast': text.hex().toLowerCase(),
        '--surface-brand-muted': `rgba(${r}, ${g}, ${b}, 0.7)`,
        '--surface-brand-line': color
    };
}
let appliedColor = null;
let appliedAccent = null;
let appliedSurface = null;
/**
 * @function applyPrimaryColor
 * @since 1.0.0
 * Sets the primary colour and its palette on the document. Invalid or empty
 * values are ignored. Cheap to call on every render.
 */
export function applyPrimaryColor(value) {
    const color = normalizeColor(value);
    if (color == null || color === appliedColor) {
        return;
    }
    const style = document.documentElement.style;
    Object.entries(primaryPalette(color)).forEach(([key, val]) => style.setProperty(key, val));
    appliedColor = color;
}
/**
 * @function clearPrimaryColor
 * @since 1.0.0
 * Removes a colour set by applyPrimaryColor, back to the stylesheet defaults.
 */
export function clearPrimaryColor() {
    const style = document.documentElement.style;
    PRIMARY_COLOR_VARIABLES.forEach((key) => style.removeProperty(key));
    appliedColor = null;
}
/**
 * @function applyVariables
 * @since 1.0.0
 * @hidden
 */
function applyVariables(color, palette, variables) {
    const style = document.documentElement.style;
    if (color == null) {
        variables.forEach((key) => style.removeProperty(key));
        return;
    }
    Object.entries(palette(color)).forEach(([key, val]) => style.setProperty(key, val));
}
/**
 * @function applyBrandColors
 * @since 1.0.0
 * Primary colour plus the optional accent and brand surface. An empty accent
 * or surface falls back to the stylesheet defaults. Cheap to call on every
 * render.
 */
export function applyBrandColors(colors) {
    applyPrimaryColor(colors.primary);
    const accent = normalizeColor(colors.accent);
    if (accent !== appliedAccent) {
        applyVariables(accent, accentPalette, ACCENT_COLOR_VARIABLES);
        appliedAccent = accent;
    }
    const surface = normalizeColor(colors.surface);
    if (surface !== appliedSurface) {
        applyVariables(surface, surfacePalette, SURFACE_COLOR_VARIABLES);
        appliedSurface = surface;
    }
}
